import { FUNDINGUUPS_ADDRESS, WBNB_ADDRESS } from "../constants";
import { gasPrice, getAccountAddress, getWriteWeb3, requireBscNetwork } from "../index";
import { ensureTokenAllowance } from "../tokens";
import { assertPayTokenSupported } from "../fundingPayTokens";
import { formatContractError } from "../contractErrors";
import { getFundingWriteContract } from "./instances";
import { fetchAllCardsCount } from "./read";

/** ABI: launchProject → 返回新项目在 getAllCards 中的下标 */
export async function launchProjectOnChain({
  payToken,
  initPriceWei,
  incrementWei,
  launchAmount,
  tokenUri,
}) {
  requireBscNetwork();
  const web3 = getWriteWeb3();
  if (!web3) throw new Error("Wallet not connected");
  const from = getAccountAddress();
  const contract = getFundingWriteContract();
  await assertPayTokenSupported(payToken);
  if (!tokenUri || !String(tokenUri).trim()) {
    throw new Error("tokenUri 为空");
  }

  const cardsBeforeLen = await fetchAllCardsCount(web3);
  const tx = contract.methods.launchProject(
    payToken,
    String(initPriceWei),
    String(incrementWei),
    String(launchAmount),
    tokenUri
  );

  try {
    await tx.call({ from });
  } catch (err) {
    throw new Error(formatContractError(err, "发行预检失败，请检查表单与支付币种"));
  }

  let gas;
  try {
    gas = await tx.estimateGas({ from });
  } catch (err) {
    throw new Error(formatContractError(err, "无法估算 gas，发行参数可能被合约拒绝"));
  }

  const gasLimit = Math.max(Math.ceil(Number(gas) * 1.25), 2800000);
  try {
    await tx.send({ from, gas: gasLimit, gasPrice });
  } catch (err) {
    throw new Error(formatContractError(err, "发行交易失败"));
  }

  const cardsAfterLen = await fetchAllCardsCount(web3);
  if (cardsAfterLen !== cardsBeforeLen + 1) {
    throw new Error("发行交易已发送，但 getAllCards 数量未增加，请稍后在链上确认");
  }
  return cardsAfterLen - 1;
}

/** ABI: crowdFundingForToken / crowdFundingForEth */
export async function crowdFundingOnChain({
  cardAddress,
  parent,
  quantity,
  payToken,
  totalPriceWei,
}) {
  requireBscNetwork();
  const web3 = getWriteWeb3();
  if (!web3) throw new Error("Wallet not connected");
  const from = getAccountAddress();
  const contract = getFundingWriteContract();
  const isBnb = payToken.toLowerCase() === WBNB_ADDRESS.toLowerCase();

  if (!isBnb) {
    await ensureTokenAllowance(payToken, FUNDINGUUPS_ADDRESS, totalPriceWei);
    const tx = contract.methods.crowdFundingForToken(
      cardAddress,
      parent,
      String(quantity)
    );
    const gas = await tx.estimateGas({ from });
    try {
      return await tx.send({
        from,
        gas: Math.ceil(Number(gas) * 1.2),
        gasPrice,
      });
    } catch (err) {
      throw new Error(formatContractError(err, "认购交易失败"));
    }
  }

  const tx = contract.methods.crowdFundingForEth(
    cardAddress,
    parent,
    String(quantity)
  );
  const gas = await tx.estimateGas({ from, value: String(totalPriceWei) });
  try {
    return await tx.send({
      from,
      value: String(totalPriceWei),
      gas: Math.ceil(Number(gas) * 1.2),
      gasPrice,
    });
  } catch (err) {
    throw new Error(formatContractError(err, "认购交易失败"));
  }
}
